import { motion } from "framer-motion";

const STAGES = ["Discovery", "Preclinical", "Phase I", "Phase II", "Phase III"];

const candidates = [
  {
    target: "Trauma Care",
    code: "LS 113",
    progress: 1, // Preclinical
    note: "Rapid hemostatic intervention for acute trauma response.",
  },
  {
    target: "Burns Wound",
    code: "HG 112",
    progress: 1, // Preclinical
    note: "Advanced hydrogel matrix for accelerated burn healing.",
  },
  {
    target: "Transdermal Delivery",
    code: "TS 541",
    progress: 0, // Discovery
    note: "Nanopolymer-driven cellular delivery for critical care.",
  },
];

const ProgressBar = ({ progress }) => {
  const pct = (progress / (STAGES.length - 1)) * 100;
  return (
    <div className="relative h-1.5 rounded-full bg-white/5 overflow-hidden">
      <motion.div
        initial={{ width: 0 }}
        whileInView={{ width: `${pct}%` }}
        viewport={{ once: true }}
        transition={{ duration: 1.4, ease: "easeOut" }}
        className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#E60000] via-[#FF2400] to-[#FF6A4D] shadow-[0_0_20px_rgba(255,36,0,0.6)]"
      />
    </div>
  );
};

export const Pipeline = () => {
  return (
    <section
      id="pipeline"
      data-testid="pipeline-section"
      className="relative section-pad bg-[#0A0A0C]"
    >
      <div className="absolute inset-x-0 top-0 divider-grad" />
      <div className="absolute inset-0 grid-bg opacity-[0.06] pointer-events-none" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        {/* Pre-headline emotional callout */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-20 max-w-4xl"
        >
          <span className="eyebrow">BECAUSE EVERY MOMENT MATTERS</span>
          <h3 className="mt-4 font-display text-3xl sm:text-5xl tracking-tighter text-white leading-tight">
            Redefining emergency care with{" "}
            <span className="italic font-light">next-gen tech.</span>
          </h3>
          <p className="mt-4 text-blude-textDim max-w-2xl text-justify">
            These innovations can mean the difference between life and loss. Our
            purpose drives every compound we develop.
          </p>
        </motion.div>

        <div className="flex items-center gap-3 mb-5">
          <div className="w-2 h-2 rounded-full bg-[#E60000]" />
          <span className="eyebrow">R&amp;D PIPELINE · 004</span>
        </div>

        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="font-display text-4xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter font-medium text-white"
          >
            From Lab <span className="italic font-light">to Life.</span>
          </motion.h2>
          <div className="text-blude-textDim text-sm max-w-md text-justify">
            Advancing smart materials for tomorrow's critical care. Candidates
            currently in discovery and early preclinical stages.
          </div>
        </div>

        {/* Dashboard */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.8 }}
          className="glass-strong rounded-2xl p-5 sm:p-8 border-white/10 relative overflow-hidden"
          data-testid="pipeline-dashboard"
        >
          {/* Top bar */}
          <div className="flex items-center justify-between pb-5 border-b border-white/5">
            <div className="flex items-center gap-3">
              <span className="dot-pulse" />
              <span className="font-mono text-[11px] tracking-[0.25em] text-white/60">
                BLUDE · MISSION CONTROL
              </span>
            </div>
            <div className="hidden sm:flex items-center gap-4 font-mono text-[11px] tracking-widest text-white/40">
              <span>STATUS · ACTIVE</span>
              <span>CYCLE · 25.Q4</span>
            </div>
          </div>

          {/* Stage Header */}
          <div className="hidden md:grid grid-cols-[1.4fr_0.7fr_2fr] gap-6 py-5 border-b border-white/5">
            <div className="font-mono text-[11px] tracking-widest text-white/40">
              TARGET INDICATION
            </div>
            <div className="font-mono text-[11px] tracking-widest text-white/40">
              CANDIDATE
            </div>
            <div className="grid grid-cols-5 gap-2">
              {STAGES.map((s) => (
                <span
                  key={s}
                  className="font-mono text-[10px] tracking-widest text-white/40 text-center"
                >
                  {s.toUpperCase()}
                </span>
              ))}
            </div>
          </div>

          {/* Rows */}
          <div className="divide-y divide-white/5">
            {candidates.map((c, idx) => (
              <motion.div
                key={c.code}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.15, duration: 0.6 }}
                className="grid md:grid-cols-[1.4fr_0.7fr_2fr] gap-6 py-6 items-center"
                data-testid={`pipeline-row-${c.code.replace(' ', '')}`}
              >
                <div>
                  <div className="font-display text-xl text-white">
                    {c.target}
                  </div>
                  <div className="text-xs text-blude-textDim mt-1">
                    {c.note}
                  </div>
                </div>
                <div className="font-mono text-sm tracking-widest text-[#FF6A4D]">
                  {c.code}
                </div>
                <div>
                  <div className="grid grid-cols-5 gap-2 mb-3">
                    {STAGES.map((s, i) => (
                      <div
                        key={s}
                        className={`h-1.5 rounded-full ${
                          i <= c.progress
                            ? "bg-gradient-to-r from-[#E60000] to-[#FF2400] shadow-[0_0_12px_rgba(255,36,0,0.6)]"
                            : "bg-white/5"
                        }`}
                      />
                    ))}
                  </div>
                  <ProgressBar progress={c.progress} />
                </div>
              </motion.div>
            ))}
          </div>

          <div className="pt-5 mt-2 flex items-center justify-between flex-wrap gap-3 text-[11px] font-mono tracking-widest text-white/40">
            <span>
              All candidates in early-stage development · clinical timelines
              subject to regulatory review.
            </span>
            <span className="text-[#FF2400]">
              ● LIVE FEED
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
